"use client";

import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { parseWRPFeaturesList } from "../../lib/function";
import { Button } from "react-bootstrap";
import { FaArrowRight, FaPlus, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { FaTimes } from "react-icons/fa";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

const allCountries = [
  { code: "us", name: "US", slug: "usa", fullName: "NY, USA" },
  { code: "ca", name: "CA", slug: "canada", fullName: "Canada" },
  { code: "ie", name: "IE", slug: "ireland", fullName: "Ireland" },
  { code: "gb", name: "UK", slug: "unitedkingdom", fullName: "United Kingdom" },
  { code: "fr", name: "FR", slug: "france", fullName: "France" },
  { code: "de", name: "DE", slug: "germany", fullName: "Germany" },
  { code: "sg", name: "SG", slug: "singapore", fullName: "Singapore" },
  { code: "au", name: "AU", slug: "australia", fullName: "Australia" },
  { code: "jp", name: "JP", slug: "japan", fullName: "Japan" },
  { code: "kr", name: "KR", slug: "korea", fullName: "Korea" },
  { code: "in", name: "IN", slug: "india", fullName: "India" },
  { code: "sa", name: "SA", slug: "saudiarabia", fullName: "Saudi Arabia" },
  { code: "ae", name: "AE", slug: "uae", fullName: "UAE" },
  { code: "my", name: "MY", slug: "malaysia", fullName: "Malaysia" },
  { code: "id", name: "ID", slug: "indonesia", fullName: "Indonesia" },
  { code: "it", name: "IT", slug: "italy", fullName: "Italy" },
];

const ServerPackagesWvps = () => {
  const [packagesData, setPackagesData] = useState<Record<string, any[]>>({});
  const [isMobile, setIsMobile] = useState(false);

  const pathname = usePathname();
  const rawSlug = pathname?.split("/").pop() || "";
  const slug = pathname?.split("-").pop();

  const selectedCountry =
    allCountries.find((country) => country.slug === slug) || allCountries[0];

  const swiperRef = useRef<SwiperType | null>(null);

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Fetch packages
  useEffect(() => {
    fetch("/api/plan", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rawSlug }),
    })
      .then((res) => res.json())
      .then((response) => {
        const mock: Record<string, any[]> = {};

        allCountries.forEach((c) => {
          mock[c.code] = response.data.slice(0, 20).map((item: any) => ({
            id: item.id.toString(),
            name: item.name,
            price: `$${item.duration.monthly.price || 0}`,
            oldPrice: `$${item.duration.monthly.actuall_price.toFixed(2)}`,
            discount:
              item.duration.monthly.discount > 0
                ? `Save ${item.duration.monthly.discount}%`
                : "0",
            term: `Price per ${item.duration.monthly.desc}`,
            features: parseWRPFeaturesList(item.description),
            configureUrl: `${process.env.NEXT_PUBLIC_API_BASE_URL}/cart.php?a=add&pid=${item.id}&billingcycle=${item.duration.monthly.type}`,
            country: c.code,
          }));
        });

        setPackagesData(mock);
      });
  }, [rawSlug]);

  /* ------------------------------------------------------------
     WHEEL SCROLL LOGIC FOR FLAGS
     - Page does NOT scroll when hovering flags
     - Wheel = slideNext or slidePrev
  ------------------------------------------------------------ */
  const attachWheelScroll = (swiper: SwiperType) => {
    swiperRef.current = swiper;

    const wrapper = swiper.wrapperEl;

    wrapper.addEventListener(
      "wheel",
      (e: WheelEvent) => {
        e.preventDefault(); // block page scroll ONLY inside flags
        if (e.deltaY > 0) swiper.slideNext();
        else swiper.slidePrev();
      },
      { passive: false }
    );
  };

  return (
    <div className="custMargin">
      <div className="container">

        {/* Header */}
        <div className="text-center my-4">
          <h1 className="display-5 fw-bold">
            Windows VPS / Cloud Servers in{" "}
            <span className="text-capitalize">{selectedCountry.fullName}</span>
          </h1>
          <p className="fs-5 fw-normal">Backed by 24/7 On-Premise Support</p>
        </div>

        {/* MOBILE DROPDOWN */}
        {isMobile && (
          <div className="dropdown w-100 mb-4">
            <button
              className="form-select p-3 fw-semibold LocCont"
              type="button"
              data-bs-toggle="dropdown"
            >
              <span className="d-flex align-items-center gap-2">
                <Image
                  src={`https://flagcdn.com/w40/${selectedCountry.code}.png`}
                  width={25}
                  height={18}
                  alt={selectedCountry.fullName}
                />
                {selectedCountry.fullName}
              </span>
              <span className="dropdown-caret" />
            </button>

            <ul className="dropdown-menu w-100 customFlagDropdown">
              {allCountries.map((c) => (
                <li
                  key={c.slug}
                  className="dropdown-item gap-2 py-2 d-flex align-items-center"
                  onClick={() =>
                    (window.location.href =
                      `/windows-virtual-private-server-vps-${c.slug}`)
                  }
                >
                  <Image
                    src={`https://flagcdn.com/w40/${c.code}.png`}
                    width={25}
                    height={18}
                    alt={c.fullName}
                  />
                  {c.fullName}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* DESKTOP FLAGS SWIPER */}
        {!isMobile && (
          <>
            <h6
              className="fw-bold fs-5 mb-3 text-center d-flex justify-content-center gap-2"
              data-bs-toggle="modal"
              data-bs-target="#wvpsCountryModal"
              style={{ cursor: "pointer" }}
            >
              All Locations <FaPlus />
            </h6>

            <div className="w-75 mx-auto mb-5">
              <div className="position-relative d-flex align-items-center">

                {/* Left Arrow */}
                <button
                  className="wvps-arrow-left"
                  style={{
                    position: "absolute",
                    left: "-15px",
                    width: "36px",
                    height: "36px",
                    borderRadius: "8px",
                    background: "#fff",
                    border: "1px solid #ddd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    zIndex: 20,
                  }}
                >
                  <FaChevronLeft />
                </button>

                {/* Right Arrow */}
                <button
                  className="wvps-arrow-right"
                  style={{
                    position: "absolute",
                    right: "-15px",
                    width: "36px",
                    height: "36px",
                    borderRadius: "8px",
                    background: "#fff",
                    border: "1px solid #ddd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    zIndex: 20,
                  }}
                >
                  <FaChevronRight />
                </button>

                {/* Swiper */}
                <div style={{ width: "100%", padding: "0 40px" }}>
                  <Swiper
                    slidesPerView="auto"
                    spaceBetween={20}
                    loop={true}
                    modules={[Navigation]}
                    navigation={{
                      nextEl: ".wvps-arrow-right",
                      prevEl: ".wvps-arrow-left",
                    }}
                    simulateTouch={false}
                    allowTouchMove={false}
                    onSwiper={attachWheelScroll} // wheel control
                  >
                    {allCountries.map((c) => {
                      const active = c.slug === slug;
                      return (
                        <SwiperSlide key={c.slug} style={{ width: "70px" }}>
                          <Link
                            href={`/windows-virtual-private-server-vps-${c.slug}`}
                            className={`text-decoration-none d-block text-center textHad py-2 countryItem ${
                              active ? "activeCountry" : ""
                            }`}
                          >
                            <Image
                              src={`https://flagcdn.com/w40/${c.code}.png`}
                              width={32}
                              height={22}
                              alt={c.fullName}
                            />
                            <div className="fw-bold mt-1">{c.name}</div>
                          </Link>
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                </div>
              </div>
            </div>
          </>
        )}

        {/* PACKAGES */}
        <div className="row justify-content-center">
          {(packagesData[selectedCountry.code] || []).map((pkg) => (
            <div key={pkg.id} className="col-lg-3 col-md-6 col-12 mb-4">
              <div className="card-body h-100">
                <h3 className="fw-bold fs-5 textOrange">{pkg.name}</h3>

                <p className="text-muted">{pkg.term}</p>

                <div className="d-flex align-items-end mb-2">
                  <span className="display-6 fw-bolder">{pkg.price}</span>
                  <span className="text-muted ms-1">/mo</span>
                </div>

                {pkg.discount !== "0" && (
                  <h4 className="fw-normal text-muted my-2">
                    <del className="fw-bold fs-5">{pkg.oldPrice}</del>
                    <span className="alert alert-warning py-1 px-2 ms-2">
                      {pkg.discount}
                    </span>
                  </h4>
                )}

                <div className="text-start mb-2 mt-3">
                  <Image
                    src={`https://flagcdn.com/w40/${pkg.country}.png`}
                    width={30}
                    height={22}
                    alt={pkg.country}
                  />
                  <span className="ms-2 fw-bold">
                    {selectedCountry.fullName}
                  </span>
                </div>

                <Link href={pkg.configureUrl}>
                  <Button
                    className="orangeBtn fw-semibold w-100"
                    size="lg"
                  >
                    ORDER <FaArrowRight />
                  </Button>
                </Link>

                <h4 className="fw-bold fs-5 mt-3">Features</h4>
                <ul className="custList list-unstyled">
                  {pkg.features.map((f: string, idx: number) => (
                    <li key={idx}>
                      <span className="text-success">✔</span>{" "}
                      <span className="fw-semibold textHad">{f}</span>
                    </li>
                  ))}
                </ul>

              </div>
            </div>
          ))}
        </div>

        {/* LOCATION MODAL */}
        <div className="modal fade" id="wvpsCountryModal" tabIndex={-1}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content p-4">
              <div className="modal-header border-0  d-flex justify-content-between">
                <h4 className="fw-bold">Choose Location</h4>
                <Button
  type="button"
  className="border-0 bg-transparent small float-end me-0"
  data-bs-dismiss="modal"
  aria-label="Close"
>
  <FaTimes className="textHad"/> 
</Button>
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  {allCountries.map((c) => (
                    <div
                      key={c.slug}
                      className="col-6 col-md-4 d-flex align-items-center gap-2"
                    >
                      <Image
                        src={`https://flagcdn.com/w40/${c.code}.png`}
                        width={30}
                        height={20}
                        alt={c.fullName}
                      />

                      <Link
                        href={`/windows-virtual-private-server-vps-${c.slug}`}
                        className="fw-semibold text-dark text-decoration-none"
                        onClick={() => {
                          const backdrop = document.querySelector(".modal-backdrop");
                          if (backdrop) backdrop.remove();
                          document.body.classList.remove("modal-open");
                          document.body.style.overflow = "";
                          document.body.style.paddingRight = "";
                        }}
                      >
                        {c.fullName}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ServerPackagesWvps;
